/* eslint-disable deprecation/deprecation */
import * as React from 'react';
import { createElement as el } from 'react';
import * as ReactDOM from 'react-dom';
import { IconState } from '../icons/Icons';
import { DropdownArrowIcon } from '../icons/resources/DropdownArrow';
import { InvertTextColors, TextColors, TextFieldHoverColor } from '../styles/Colors';
import { DefaultFocusIndicator } from '../styles/FocusIndicators';
import { PreventUserSelect } from '../styles/PreventUserSelect';
import { TabStyles } from '../styles/TabStyles';
import { TextEllipsis } from '../styles/TextEllipsis';
import { DataAttributes } from '../utils/DataAttributes';
import { conditional, isAlphabeticChar, makeTestId } from '../utils/Declarative';
import { generateId } from '../utils/DomUtil';
import { findIndex } from '../utils/FindIndex';
import { InteractiveDomHoCDecorator, InteractiveDomProps, InteractiveDomState, InteractiveStyles } from '../utils/InteractiveDom';
import { HorizontalOriginOption, VerticalOriginOption } from '../utils/Overlay';
import { GenericFlyoutListMenuProps, GenericFlyoutListMenuWidget } from './FlyoutListMenuWidget';
import { GenericMenuItemInfo } from './ListMenuWidget';

const { Sizing, Colors, Spacing, Typography } = TabStyles;
const SidePadding = 8;

export enum DropdownType {
  Outline,
  Line,
  Text
}

interface DropdownStyle {
  container: React.CSSProperties;
  button: { base: React.CSSProperties, interactive: InteractiveStyles };
  value: React.CSSProperties;
  iconContainer: React.CSSProperties;
  icon: React.CSSProperties;
  label: React.CSSProperties;
}

interface LineColors {
  normal: string;
  hover: string;
  active: string;
  focus: string;
  disabled: string;
}

interface TextColors {
  normal: string;
  active: string;
  disabled: string;
}

interface DropdownStyleOptions {
  innerRowHeight: number;
  primaryLineColors: LineColors;
  primaryTextColors: TextColors;
  hoverBackgroundColor: React.CSSProperties['backgroundColor'];
}

function getLabelStyle(options: DropdownStyleOptions): React.CSSProperties {
  return {
    display: 'block',
    fontSize: Typography.Sizing.Default,
    fontWeight: 'normal',
    color: options.primaryTextColors.normal,
    lineHeight: `19px`,
  };
}

function getOutlineDropdownStyle(options: DropdownStyleOptions): DropdownStyle {
  return {
    container: {},
    button: {
      base: {
        ...TextEllipsis,
        ...PreventUserSelect,
        backgroundColor: 'transparent',
        borderColor: options.primaryLineColors.normal,
        borderRadius: Sizing.BorderRadius,
        borderStyle: 'solid',
        borderWidth: Sizing.BorderWidth,
        color: options.primaryTextColors.normal,
        fontSize: Typography.Sizing.Default,
        fontWeight: 'normal',
        height: Sizing.RowHeight,
        outline: 'none',
        overflow: 'hidden',
        paddingLeft: SidePadding - Sizing.BorderWidth,
        paddingRight: Spacing.LargeSpace + SidePadding - Sizing.BorderWidth,
      },
      interactive: {
        hover: {
          backgroundColor: options.hoverBackgroundColor,
          borderColor: options.primaryLineColors.hover,
        },
        active: {
          borderColor: options.primaryLineColors.active
        },
        focusVisible: DefaultFocusIndicator() as React.CSSProperties,
        disabled: {
          borderColor: options.primaryLineColors.disabled,
          color: options.primaryTextColors.disabled
        }
      }
    },
    value: {
      lineHeight: `${options.innerRowHeight}px`
    },
    iconContainer: {
      display: 'inline-block',
      position: 'absolute',
      right: 0,
    },
    icon: {
      marginTop: (options.innerRowHeight - DropdownArrowIcon.height) / 2
    },
    label: getLabelStyle(options)
  };
}

function getLineDropdownStyle(options: DropdownStyleOptions): DropdownStyle {
  return {
    container: {},
    button: {
      base: {
        ...TextEllipsis,
        ...PreventUserSelect,
        backgroundColor: 'transparent',
        borderBottomColor: options.primaryLineColors.normal,
        borderBottomStyle: 'solid',
        borderBottomWidth: Sizing.BorderWidth,
        borderLeft: 'none',
        borderRight: 'none',
        borderTop: 'none',
        color: options.primaryTextColors.normal,
        fontSize: Typography.Sizing.Default,
        fontWeight: 'normal',
        height: Sizing.RowHeight,
        outline: 'none',
        overflow: 'hidden',
        paddingLeft: SidePadding - Sizing.BorderWidth,
        paddingRight: Spacing.LargeSpace + SidePadding - Sizing.BorderWidth,
      },
      interactive: {
        hover: {
          backgroundColor: options.hoverBackgroundColor,
          borderBottomColor: options.primaryLineColors.hover,
        },
        active: {
          borderBottomColor: options.primaryLineColors.active
        },
        focusVisible: DefaultFocusIndicator() as React.CSSProperties,
        disabled: {
          borderBottomColor: options.primaryLineColors.disabled,
          color: options.primaryTextColors.disabled
        }
      }
    },
    value: {
      top: Sizing.BorderWidth,
      lineHeight: `${options.innerRowHeight}px`,
    },
    iconContainer: {
      bottom: 1,
      position: 'absolute',
      right: 1,
      top: 1,
    },
    icon: {
      marginTop: (options.innerRowHeight - DropdownArrowIcon.height) / 2
    },
    label: getLabelStyle(options),
  };
}

function getTextDropdownStyle(options: DropdownStyleOptions): DropdownStyle {
  return {
    container: {
      display: 'inline-block',
      width: 'auto'
    },
    button: {
      base: {
        ...PreventUserSelect,
        backgroundColor: 'transparent',
        border: 'none',
        color: options.primaryTextColors.normal,
        cursor: 'pointer',
        display: 'inline-block',
        fontSize: Typography.Sizing.Default,
        fontWeight: 'normal',
        height: Sizing.RowHeight,
        lineHeight: `${Sizing.RowHeight}px`,
        overflow: 'hidden',
        paddingLeft: SidePadding,
        paddingRight: DropdownArrowIcon.width,
      },
      interactive: {
        hover: {
          backgroundColor: options.hoverBackgroundColor,
        },
        active: {
          color: options.primaryTextColors.active
        },
        disabled: {
          color: options.primaryTextColors.disabled,
          cursor: 'default',
        },
        focusVisible: DefaultFocusIndicator() as React.CSSProperties,
      }
    },
    value: {
      lineHeight: 'inherit'
    },
    iconContainer: {
      position: 'absolute'
    },
    icon: {
      marginTop: (Sizing.RowHeight - DropdownArrowIcon.height) / 2
    },
    label: getLabelStyle(options),
  };
}

const getStyles: () => {
  OutlineDropdownStyle: DropdownStyle,
  OutlineDropdownDarkBackgroundStyle: DropdownStyle,
  LineDropdownStyle: DropdownStyle,
  LineDropdownDarkBackgroundStyle: DropdownStyle,
  TextDropdownStyle: DropdownStyle,
  TextDropdownDarkBackgroundStyle: DropdownStyle,
} = () => {
  const innerRowHeight = Sizing.RowHeight - (Sizing.BorderWidth * 2);

  const normalPrimaryLineColors: LineColors = {
    normal: Colors.L6,
    hover: Colors.L8,
    active: Colors.L9,
    focus: Colors.Focus,
    disabled: Colors.L9Disabled
  };

  const normalPrimaryTextColors: TextColors = {
    normal: TextColors.Primary,
    disabled: TextColors.Disabled,
    active: Colors.Black,
  };

  const darkBackgroundPrimaryLineColors: LineColors = {
    normal: Colors.WhiteTransparentLine,
    hover: Colors.WhiteTransparentLineHover,
    active: Colors.White,
    focus: Colors.White,
    disabled: Colors.WhiteDisabled
  };
  const darkBackgroundPrimaryTextColors: TextColors = {
    normal: InvertTextColors.Primary,
    disabled: InvertTextColors.Disabled,
    active: Colors.White,
  };

  const normalOptions: DropdownStyleOptions = {
    innerRowHeight: innerRowHeight,
    primaryLineColors: normalPrimaryLineColors,
    primaryTextColors: normalPrimaryTextColors,
    hoverBackgroundColor: TextFieldHoverColor,
  };

  const darkBackgroundOptions: DropdownStyleOptions = {
    innerRowHeight: innerRowHeight,
    primaryLineColors: darkBackgroundPrimaryLineColors,
    primaryTextColors: darkBackgroundPrimaryTextColors,
    hoverBackgroundColor: 'transparent',
  };

  const OutlineDropdownStyle = getOutlineDropdownStyle(normalOptions);
  const OutlineDropdownDarkBackgroundStyle = getOutlineDropdownStyle(darkBackgroundOptions);
  const LineDropdownStyle = getLineDropdownStyle(normalOptions);
  const LineDropdownDarkBackgroundStyle = getLineDropdownStyle(darkBackgroundOptions);
  const TextDropdownStyle = getTextDropdownStyle(normalOptions);
  const TextDropdownDarkBackgroundStyle = getTextDropdownStyle(darkBackgroundOptions);

  return {
    OutlineDropdownStyle,
    OutlineDropdownDarkBackgroundStyle,
    LineDropdownStyle,
    LineDropdownDarkBackgroundStyle,
    TextDropdownStyle,
    TextDropdownDarkBackgroundStyle
  };
};

export function getDropdownStylesByType(dropdownType: DropdownType, onDarkBackground?: boolean): DropdownStyle {
  const styles = getStyles();
  switch (dropdownType) {
    case DropdownType.Outline:
      return onDarkBackground ? styles.OutlineDropdownDarkBackgroundStyle : styles.OutlineDropdownStyle;
    case DropdownType.Line:
      return onDarkBackground ? styles.LineDropdownDarkBackgroundStyle : styles.LineDropdownStyle;
    case DropdownType.Text:
      return onDarkBackground ? styles.TextDropdownDarkBackgroundStyle : styles.TextDropdownStyle;
    default:
      throw new Error(`DropdownWidget attempted to render invalid DropdownType: ${dropdownType}`);
  }
}

export interface DropdownButtonProps extends InteractiveDomProps {
  dropdownType: DropdownType;
  dropdownOpen: boolean;
  dropdownId: string;
  onKeyDown: React.KeyboardEventHandler<HTMLDivElement>;
  testId: string;
  labelledById?: string;
  onDarkBackground?: boolean;
  /**
   * Tooltip text to be shwon by the browser when user hovers the element
   */
  title?: string;
}

@InteractiveDomHoCDecorator
export class DropdownButtonWidget extends React.Component<DropdownButtonProps, InteractiveDomState> {
  public componentDidUpdate(): void {
    // We lose focus on spacebar/enter keydown, so the active state needs to be manually set
    if (this.props.dropdownOpen && this.state.active) {
      this.setState({ active: false });
    }
  }

  private getDropdownIconState(): IconState {
    return {
      active: this.state.active || this.props.dropdownOpen,
      disabled: this.props.disabled,
      hover: this.state.hover,
      onDarkBackground: this.props.onDarkBackground,
    };
  }

  public render(): JSX.Element {
    const dropdownStyles: DropdownStyle = getDropdownStylesByType(this.props.dropdownType, this.props.onDarkBackground);
    const buttonStyles: React.CSSProperties = {
      ...dropdownStyles.button.base,
      boxSizing: 'border-box',
      position: 'relative',
      ...conditional(this.props.dropdownOpen, dropdownStyles.button.interactive.active)
    };

    const blockProps: React.SelectHTMLAttributes<HTMLDivElement> & DataAttributes = {
      'aria-atomic': true,
      'aria-expanded': this.props.dropdownOpen,
      'aria-haspopup': 'listbox',
      'aria-labelledby': this.props.labelledById,
      'aria-live': (this.state.focus ? 'assertive' : 'off'),
      'aria-controls': (this.props.dropdownOpen ? this.props.dropdownId : undefined),
      'aria-relevant': 'text',
      'data-tb-test-id': this.props.testId,
      disabled: this.props.disabled,
      onKeyDown: this.props.onKeyDown,
      role: 'combobox',
      style: buttonStyles,
      tabIndex: this.props.disabled ? undefined : 0,
      title: this.props.title,
    };

    const spanProps = {
      style: dropdownStyles.value,
      title: conditional(typeof this.props.children === 'string', this.props.children)
    };

    return el('div', blockProps,
      el('span', spanProps,
        this.props.children
      ),
      el('div', { style: dropdownStyles.iconContainer },
        el(DropdownArrowIcon.icon, {
          style: dropdownStyles.icon,
          ...this.getDropdownIconState(),
        })
      )
    );
  }
}

export interface DropdownState {
  buttonText: string;
  isOpen: boolean;
}

export interface BaseDropdownProps extends Pick<GenericFlyoutListMenuProps, 'scrollingRootId'> {
  /** Optional callback to be invoked when dropdown is closed */
  onClose?: () => void;

  /** Optional callback to be invoked when dropdown is opened */
  onOpen?: () => void;

  /** Function that's called when an item in the menu is selected, this will be called even if the selected item hasn't changed */
  onSelect: (value: string, fromFlyoutMenu?: boolean) => void;

  /** The style type of the dropdown - use enum DropdownType */
  dropdownType: DropdownType;

  /** Does the widget appear on a dark background? */
  onDarkBackground?: boolean;

  /** The value of the selected item.  Should match the value given in a given MenuItemInfo */
  selectedValue?: string;

  /** If the dropdown is disabled */
  disabled?: boolean;

  /** What will be displayed in the element if there is no selectedValue */
  placeholder?: string;

  /** The label above the element - only available for Line type dropdowns */
  label?: string;

  /** Max height of the dropdown menu in pixels.  Menu will be scrollable if contents exceed this height. */
  maxHeight?: number;

  /** Style overrides for the button, such as width of the control. It also applies to flyout if specific flyoutStyle not specified. */
  containerStyle?: React.CSSProperties;

  /** Style overrides for the flyout, such as width of the flyout.  */
  flyoutStyle?: React.CSSProperties;

  /**
  * The stacking root is the container in which a div element (mount point) will be appended.  The stacking element
  * will then render to the mount point. If no stackingRootId is provided it will default to the DefaultRootContainer from
  * SharedWidgetsConfig which defaults to document.body
  */
  stackingRootId?: string;

  /**
   * VizClient uses z-indexing in several places to order stacking. This prop should be avoided otherwise.
   */
  zIndex?: number;

  title?: string;

  testId: string;

  /**
   * @deprecated Whether we should return the focus to the previous focused element or not, when the flyout list is dismissed (always true)
   */
  shouldReturnFocusOnClose?: boolean;
}

export interface GenericDropdownProps extends BaseDropdownProps {
  /** The contents of the menu as an array of MenuItemInfos */
  menuItemInfos: GenericMenuItemInfo[];
}

/**
 * @deprecated use `SingleSelectDropdown`
 */
export class GenericDropdownWidget extends React.Component<GenericDropdownProps, DropdownState> {
  private buttonEl: HTMLElement | null = null;
  private enabledItems: GenericMenuItemInfo[] = [];
  private readonly labelId: string = generateId();
  private readonly dropdownId: string = generateId();

  public static defaultProps: Pick<GenericDropdownProps, 'shouldReturnFocusOnClose'> = {
    shouldReturnFocusOnClose: true,
  };

  public constructor(props: GenericDropdownProps) {
    super(props);
    if (props.menuItemInfos.filter(item => !!item.menuItemInfos).length > 0) {
      throw Error('Dropdowns may not contain submenus');
    }

    this.setEnabledItems(this.props.menuItemInfos);
    this.state = {
      buttonText: this.getButtonDisplayValue(),
      isOpen: false
    };
  }

  public componentDidUpdate(): void {
    this.setEnabledItems(this.props.menuItemInfos);
    const buttonText = this.getButtonDisplayValue();
    if (buttonText !== this.state.buttonText) {
      this.setState({ buttonText });
    }
  }

  public getMenuWidth(): number | 'auto' | undefined {
    let potentialWidth: number | undefined = undefined;
    if (this.props.flyoutStyle) {
      const flyoutWidth = this.props.flyoutStyle.width;
      if (flyoutWidth === 'auto') {  // special-case: want dynamically-sized flyout but it will evaluate to 0 when parsing as int
        return 'auto';
      }
      potentialWidth = parseInt(flyoutWidth as string, 10);
    } else if (this.props.containerStyle) {
      potentialWidth = parseInt(this.props.containerStyle.width as string, 10);
    }

    if (this.buttonEl) {
      const boundingWidth: number = this.buttonEl.getBoundingClientRect().width;
      potentialWidth = Math.max(boundingWidth, potentialWidth || 0);
    }

    return potentialWidth;
  }

  private getIndexOfSelectedItem(): number {
    return findIndex(this.enabledItems,
      (item: GenericMenuItemInfo) => item.value === this.props.selectedValue,
      0, this
    );
  }

  private setEnabledItems(menuItemInfos: GenericMenuItemInfo[]): void {
    this.enabledItems = (menuItemInfos || []).filter(item =>
      // eslint-disable-next-line deprecation/deprecation
      !item.isDivider && !item.disabled && !item.isUnselectable
    );
  }

  private toggleMenu = (): void => {
    if (this.props.disabled) { return; }
    this.setState((prevState, props) => {
      const isNowOpen = !prevState.isOpen;
      if (isNowOpen) {
        if (props.onOpen) { props.onOpen(); }
      } else {
        if (props.onClose) { props.onClose(); }
      }

      return { isOpen: isNowOpen };
    });
  }

  private handleSelect = (value: string): void => {
    this.props.onSelect(value, this.state.isOpen);
    this.onRequestClose();
  }

  // Handle key presses as defined by ARIA https://www.w3.org/TR/wai-aria-practices/#menu
  private handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (this.props.disabled || this.state.isOpen || e.ctrlKey || e.altKey || e.metaKey) {
      return;
    }
    const selectedIndex = this.getIndexOfSelectedItem();

    if (isAlphabeticChar(e.key)) {
      const nextIndexOfFirstLetter: number = findIndex(this.enabledItems,
        (item: GenericMenuItemInfo) => {
          return !!item.keyboardAccessibleValue &&
            item.keyboardAccessibleValue.charAt(0).toUpperCase() === e.key.toUpperCase();
        }, selectedIndex + 1, this);
      if (nextIndexOfFirstLetter > -1) {
        this.props.onSelect(this.enabledItems[nextIndexOfFirstLetter].value!, this.state.isOpen);
      }
    } else {
      let value: string | undefined = undefined;
      switch (e.key) {
        case ' ':
        case 'Enter':
          this.toggleMenu();
          break;
        case 'ArrowUp':
          if (selectedIndex > 0) {
            value = this.enabledItems[selectedIndex - 1].value;
          }
          break;
        case 'ArrowDown':
          if (selectedIndex < this.enabledItems.length - 1) {
            value = this.enabledItems[selectedIndex + 1].value;
          }
          break;
        case 'Home':
          value = this.enabledItems[0].value;
          break;
        case 'End':
          value = this.enabledItems[this.enabledItems.length - 1].value;
          break;
        case 'Escape':
          // swallow ESC events that plausibly originate from focus being returned to the button after the popup closed
          if (e.repeat) {
            break;
          } else {
            return;
          }
        default:
          // Do not prevent default or stopPropagation on non-handled key presses
          return;
      }

      if (value) {
        this.props.onSelect(value, this.state.isOpen);
      }

      e.preventDefault();
      e.stopPropagation();
    }
  }

  private onRequestClose = (): void => {
    this.toggleMenu();
  }

  private getButtonDisplayValue(): string {
    const selectedItemIndex = this.getIndexOfSelectedItem();
    return selectedItemIndex > -1 ? (this.enabledItems[selectedItemIndex].textForDropdownButton || '') : (this.props.placeholder || '');
  }

  private setButtonElement = (ref: DropdownButtonWidget): void => {
    // eslint-disable-next-line react/no-find-dom-node
    this.buttonEl = ReactDOM.findDOMNode(ref) as HTMLElement;
  }

  private getAnchorClientRect = (): ClientRect | undefined => {
    return this.buttonEl?.getBoundingClientRect();
  }

  public render(): JSX.Element {
    const dropdownStyles = getDropdownStylesByType(this.props.dropdownType, this.props.onDarkBackground);
    const shouldShowLabel = !!this.props.label;
    const containerStyle = { ...this.props.containerStyle, ...dropdownStyles.container };
    const flyoutWidth = this.getMenuWidth();
    const flyoutStyle = { ...this.props.flyoutStyle, width: flyoutWidth };

    const focusIndex = (this.state.isOpen && this.props.selectedValue) ?
      findIndex(this.props.menuItemInfos, (item) => item.value === this.props.selectedValue) :
      0;

    return (
      el('div', {
        style: containerStyle,
        'data-tb-test-id': makeTestId(this.props.testId, 'Dropdown')
      },
        conditional(shouldShowLabel,
          el('label', {
            style: dropdownStyles.label,
            id: this.labelId
          },
            this.props.label
          )
        ),
        el('div', {},
          el(DropdownButtonWidget, {
            disabled: this.props.disabled,
            dropdownId: this.dropdownId,
            dropdownOpen: this.state.isOpen,
            dropdownType: this.props.dropdownType,
            onDarkBackground: this.props.onDarkBackground,
            interactiveStyles: dropdownStyles.button.interactive,
            onKeyDown: this.handleKeyDown,
            pointerClick: this.toggleMenu,
            labelledById: conditional(shouldShowLabel, this.labelId),
            ref: this.setButtonElement,
            testId: makeTestId(this.props.testId, 'DropdownButton'),
            title: this.props.title
          }, this.state.buttonText)
        ),
        conditional(this.state.isOpen,
          el(GenericFlyoutListMenuWidget, {
            toggleEl: this.buttonEl,
            getAnchorClientRect: this.getAnchorClientRect,
            stackingRootId: this.props.stackingRootId,
            // eslint-disable-next-line deprecation/deprecation
            shouldReturnFocusToToggleEl: this.props.shouldReturnFocusOnClose,
            anchorOrigin: {
              vertical: VerticalOriginOption.bottom,
              horizontal: HorizontalOriginOption.left
            },
            allowAutoPosition: true,
            // eslint-disable-next-line deprecation/deprecation
            scrollingRootId: this.props.scrollingRootId,
            handleScrollOnScrollingRoot: this.toggleMenu,
            onSelect: this.handleSelect,
            id: this.dropdownId,
            __INTERNAL__focusIndex: focusIndex,
            role: 'listbox',
            maxHeight: this.props.maxHeight,
            menuStyle: flyoutStyle,
            menuItemInfos: this.props.menuItemInfos,
            requestCloseAll: this.onRequestClose,
            zIndex: this.props.zIndex,
            testId: this.props.testId,
          })
        )
      )
    );
  }
}
